/**
 * Date utility functions for consistent date formatting across the application
 * All dates are formatted as yyyy-mm-dd without time components for timezone consistency
 */

/**
 * Decode URL-encoded timezone string
 * @param timezone - Potentially URL-encoded timezone string
 * @returns Decoded timezone string
 */
function decodeTimezone(timezone: string): string {
  try {
    return decodeURIComponent(timezone);
  } catch (error) {
    console.warn(`Failed to decode timezone ${timezone}, using as-is:`, error);
    return timezone;
  }
}

/**
 * Format a Date object to yyyy-mm-dd string format
 * @param date - Date object to format
 * @param timezone - Optional timezone string for timezone-aware formatting
 * @returns Formatted date string in yyyy-mm-dd format
 */
export function formatDateToYYYYMMDD(date: Date, timezone?: string): string {
  if (timezone) {
    try {
      const decodedTimezone = decodeTimezone(timezone);
      return date.toLocaleDateString("en-CA", { timeZone: decodedTimezone }); // en-CA gives YYYY-MM-DD format
    } catch (error) {
      console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    }
  }
  return date.toISOString().split("T")[0];
}

/**
 * Parse YYYY-MM-DD as a calendar date without UTC midnight interpretation.
 * `new Date("YYYY-MM-DD")` is parsed as UTC and shifts the local calendar day in many timezones.
 */
export function parseYYYYMMDDLocal(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return new Date(NaN);
  return new Date(year, month - 1, day);
}

/** Format a local calendar Date as YYYY-MM-DD without `toISOString()` UTC day shifts. */
export function formatDateToYYYYMMDDLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Format a date to a readable string
 * @param date - Date object to format
 * @param timezone - Optional timezone string for timezone-aware formatting
 * @returns Formatted date string
 */
export function formatDate(date: Date, timezone?: string): string {
  if (timezone) {
    try {
      const decodedTimezone = decodeTimezone(timezone);
      return date.toLocaleDateString("en-US", { timeZone: decodedTimezone });
    } catch (error) {
      console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    }
  }
  return date.toLocaleDateString("en-US");
}

/**
 * Get date range for a specified number of days back
 * @param daysBack - Number of days to go back from today
 * @param timezone - Optional timezone string for timezone-aware date calculation
 * @returns Object with start_date and end_date in YYYY-MM-DD format
 */
export function getDateRange(
  daysBack: number,
  timezone?: string,
): { start_date: string; end_date: string } {
  const end_date = timezone ? getTodayInCompanyTimezone(timezone) : new Date();
  const start_date = new Date(end_date);
  start_date.setDate(end_date.getDate() - daysBack);

  return {
    start_date: formatDateToYYYYMMDD(start_date, timezone),
    end_date: formatDateToYYYYMMDD(end_date, timezone),
  };
}

/**
 * Get date range for a specified number of months back
 * @param monthsBack - Number of months to go back from today
 * @param timezone - Optional timezone string for timezone-aware date calculation
 * @returns Object with start_date and end_date in YYYY-MM-DD format
 */
export function getMonthsBackRange(
  monthsBack: number,
  timezone?: string,
): { start_date: string; end_date: string } {
  const end_date = timezone ? getTodayInCompanyTimezone(timezone) : new Date();
  const start_date = new Date(end_date);
  start_date.setMonth(end_date.getMonth() - monthsBack);

  return {
    start_date: formatDateToYYYYMMDD(start_date, timezone),
    end_date: formatDateToYYYYMMDD(end_date, timezone),
  };
}

/**
 * Format a date string to yyyy-mm-dd format
 * @param dateString - Date string to format
 * @returns Formatted date string in yyyy-mm-dd format
 */
export function formatDateStringToYYYYMMDD(dateString: string): string {
  const date = new Date(dateString);
  return formatDateToYYYYMMDD(date);
}

/**
 * Get current date in yyyy-mm-dd format
 * @returns Current date in yyyy-mm-dd format
 */
export function getCurrentDateYYYYMMDD(): string {
  return formatDateToYYYYMMDD(new Date());
}

/**
 * Get date from X days ago in yyyy-mm-dd format
 * @param daysAgo - Number of days ago
 * @returns Date in yyyy-mm-dd format
 */
export function getDateFromDaysAgoYYYYMMDD(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return formatDateToYYYYMMDD(date);
}

/**
 * Get yesterday's date in YYYY-MM-DD format
 * @param timezone - Optional timezone string for timezone-aware date calculation
 * @returns Date string in YYYY-MM-DD format
 */
export function getYesterdayYYYYMMDD(timezone?: string): string {
  const today = timezone ? getTodayInCompanyTimezone(timezone) : new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return formatDateToYYYYMMDD(yesterday, timezone);
}

/**
 * Get date from X days ago in yyyy-mm-dd format
 * @param daysAgo - Number of days ago
 * @returns Date in yyyy-mm-dd format
 */
export function getDateFromDaysAgo(daysAgo: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date;
}

/**
 * Create a Date object from yyyy-mm-dd string
 * @param dateString - Date string in yyyy-mm-dd format
 * @param timezone - Optional timezone string for timezone-aware date creation
 * @returns Date object
 */
export function createDateFromYYYYMMDD(dateString: string, timezone?: string): Date {
  if (timezone) {
    try {
      const decodedTimezone = decodeTimezone(timezone);
      // Create date in company timezone
      const dateStringWithTime = `${dateString}T00:00:00`;
      const utcDate = new Date(dateStringWithTime + "Z"); // Add Z to make it UTC

      // Convert to company timezone
      const companyDate = new Date(utcDate.toLocaleString("en-US", { timeZone: decodedTimezone }));
      return companyDate;
    } catch (error) {
      console.warn(`Invalid timezone ${timezone} or date ${dateString}, using local date:`, error);
    }
  }

  // Parse the date string and create a date in local timezone
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
}

/**
 * Check if a date string is in yyyy-mm-dd format
 * @param dateString - Date string to check
 * @returns True if in yyyy-mm-dd format
 */
export function isValidYYYYMMDD(dateString: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;

  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

// Helper function to get today in company timezone
function getTodayInCompanyTimezone(timezone: string): Date {
  try {
    const decodedTimezone = decodeTimezone(timezone);
    const now = new Date();
    const todayString = now.toLocaleDateString("en-CA", { timeZone: decodedTimezone }); // YYYY-MM-DD format
    const [year, month, day] = todayString.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  } catch (error) {
    console.warn(`Invalid timezone ${timezone}, using local date:`, error);
    return new Date();
  }
}

export function getPreviousMonth(date: Date): Date {
  const prev = new Date(date);
  prev.setDate(1); // Set to 1st temporarily to avoid overflow
  prev.setMonth(prev.getMonth() - 1);

  const daysInMonth = new Date(prev.getFullYear(), prev.getMonth() + 1, 0).getDate();
  const originalDay = date.getDate();

  prev.setDate(Math.min(originalDay, daysInMonth));
  return prev;
}

// Cached equivalents of toLocaleDateString("en-US") and the 24-hour toLocaleTimeString below; the
// per-call variants rebuilt an Intl formatter for every date cell of a large sheet export.
const enUsDateFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
});
const enUs24HourTimeFormat = new Intl.DateTimeFormat("en-US", {
  hour12: false,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

export function formatDateTimeTo12Hour(dateString: string | null | undefined): string {
  if (!dateString) {
    return "-";
  }

  // Handle different date formats
  let datePart: string;
  let timePart: string;

  // Check if it's in the expected format "MM/DD/YYYY, HH:MM:SS"
  if (dateString.includes(", ")) {
    [datePart, timePart] = dateString.split(", ");
  } else {
    // Try to parse as a Date object and format it
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      // Format as MM/DD/YYYY, HH:MM:SS
      datePart = enUsDateFormat.format(date);
      timePart = enUs24HourTimeFormat.format(date);
    } catch {
      return "-";
    }
  }

  if (!timePart) {
    return datePart || "-";
  }

  const [hourStr, minuteStr, secondStr] = timePart.split(":");
  let hour = parseInt(hourStr, 10);

  if (isNaN(hour)) {
    return datePart || "-";
  }

  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // Convert 0 -> 12, 13 -> 1, etc.

  const formattedTime = `${hour}:${minuteStr}:${secondStr} ${ampm}`;
  return `${datePart}, ${formattedTime}`;
}

export function daysAfter(date: Date) {
  const givenDate = new Date(date);
  const today = new Date();

  const utcGiven = Date.UTC(givenDate.getFullYear(), givenDate.getMonth(), givenDate.getDate());
  const utcToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  const diffMs = utcToday - utcGiven;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function formatRetentionDays(totalDays: number) {
  if (totalDays < 30) return `${totalDays} days`;

  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;

  if (days === 0) return `${months} months`;
  return `${months} months ${days} days`;
}
